import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Col, notification, Row } from "antd";
import { isMobile } from "react-device-detect";
import { AlertCircleOutlineIcon, ArrowBackIcon, ChevronRightIcon, StarIcon, StarOutlineIcon, HomeIcon } from "mdi-react";
import { uniqBy } from "lodash";
import store from "../../../store";

// styling
import { css } from "emotion";
import colors from "../../../style/colors";

// hooks
import useOverviewData from "./hooks/useOverviewData";

// actions
import { hideModalPage, showModalPage, showDialog } from "../../../actions/uiActions";

// ui-Components
import ActionWrapper from "../../ui/ActionWrapper";
import InlineSpinner from "../../ui/InlineSpinner";
import DropDown from "../../ui/DropDown";
import Page from "../../ui/Page";
import Button from "../../ui/Button";
import TabView from "../../ui/TabView";
import TabBar from "../../ui/TabBar";
import SearchInput from "../../ui/SearchInput";
import usePageAndBackButtonURL from "../../../hooks/usePageAndBackButtonURL";
import TopBar from "../../ui/TopBar";
import pageNavigator from "../../../utilities/page-navigator";
import StatusBox from "../../ui/StatusBox";
import ListItem from "../../ui/ListItem";
import PunchModal from "./PunchModal";
import punchStatuses from "../../../config/punchStatuses";
import PunchStatus from "./PunchStatus";
import AccessCheck from "../../ui/AccessCheck";

import Box from "@mui/material/Box";
import MuiButton from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";

// utilities
import req from "../../../utilities/request-utility";
import highlightMatch from "../../../utilities/highlight-match";

import common from "../../../style/common";

const downloadModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "10px",
  textAlign: "center",
};

let timeout;
function PunchOverview(props) {
  const { hideModalPage, match, selectedProject, selectedProjectArea, showDialog, showModalPage, user } = props;

  const [filterData, setFilterData] = useState({
    punchList: "",
    punchOwner: "",
    punchOrigin: "",
    punchLocation: "",
    punchCategory: "",
    area: selectedProjectArea ? (selectedProjectArea.id ? selectedProject.id : "") : "",
    system: "",
    searchterm: "",
    user: "",
    assignee: "",
  });

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const [activeFavoriteIds, setActiveFavoriteIds] = useState([]);
  const [exportLoading, setExporting] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const { backButtonURL } = usePageAndBackButtonURL(props);
  const {
    openPunches,
    clearedPunches,
    closedPunches,
    favoritedPunches,
    assignedToYouPunches,
    statusCounts,
    getStatusCounts,
    metaData,
    metaDataRefetch,
    getData,
  } = useOverviewData();

  const [assignedNotifications, setAssignedNotifications] = useState(
    metaData.data.punchNotifications ? metaData.data.punchNotifications.notifications : 0
  );

  const [assignees, setAssignees] = useState(
    metaData.data.assignees
      ? uniqBy(
          metaData.data.assignees.map((assignee) => ({ label: assignee.name, value: assignee.id })),
          "label"
        )
      : undefined
  );

  const tabs = [
    {
      title: (
        <PunchStatus
          count={statusCounts.data.open ? statusCounts.data.open : 0}
          offsetBottom={false}
          isLoading={statusCounts.loading}
          status={punchStatuses.open}
        />
      ),
      color: colors.red,
    },
    {
      title: (
        <PunchStatus
          count={statusCounts.data.cleared ? statusCounts.data.cleared : 0}
          offsetBottom={false}
          isLoading={statusCounts.loading}
          status={punchStatuses.cleared}
        />
      ),
      color: colors.vividBlue,
    },
    {
      title: (
        <PunchStatus
          count={statusCounts.data.closed ? statusCounts.data.closed : 0}
          offsetBottom={false}
          isLoading={statusCounts.loading}
          status={punchStatuses.closed}
        />
      ),
      color: colors.green,
    },
    {
      title: (
        <PunchStatus
          count={statusCounts.data.favorites ? statusCounts.data.favorites : 0}
          offsetBottom={false}
          isLoading={statusCounts.loading}
          status={punchStatuses.favorites}
        />
      ),
      color: "#103447",
    },
    {
      title: (
        <PunchStatus
          count={statusCounts.data.assignees ? statusCounts.data.assignees : 0}
          offsetBottom={false}
          isLoading={statusCounts.loading}
          status={punchStatuses.assignedToYou}
          notifications={assignedNotifications}
        />
      ),
      color: "#103447",
    },
  ];

  const checkExistingExportRequest = async () => {
    try {
      const storedRequestId = localStorage.getItem("favoriteExportRequestId");
      if (!storedRequestId) return;

      const { data: response } = await req().get(`semcompletion/v2/punches/favorite/export-request/${storedRequestId}`);

      if (response.status === "generating") {
        setIsGenerating(true);
        checkExportStatus(storedRequestId);
      } else {
        // If the request is done or failed, clear the stored ID
        localStorage.removeItem("favoriteExportRequestId");
      }
    } catch (error) {
      console.log("%c Line:328 🌭 error checking existing request", "color:#ed9ec7", error);
      // If there's an error, clear the stored ID
      localStorage.removeItem("favoriteExportRequestId");
    }
  };

  useEffect(() => {
    (async () => {
      const punchActiveTabIndex = localStorage.getItem("punchActiveTabIndex");
      if (punchActiveTabIndex !== null) setActiveTabIndex(parseInt(punchActiveTabIndex));

      const punchFilterData = localStorage.getItem("punchFilterData");
      let initialFilterData = filterData;

      if (punchFilterData) {
        initialFilterData = JSON.parse(punchFilterData);
        setFilterData(JSON.parse(punchFilterData));
      }

      await Promise.all([
        getData({ tabIndex: 0, filterData: initialFilterData }),
        getData({ tabIndex: 1, filterData: initialFilterData }),
        getData({ tabIndex: 2, filterData: initialFilterData }),
        getData({ tabIndex: 3, filterData: initialFilterData }),
        getData({ tabIndex: 4, filterData: initialFilterData }),
        checkExistingExportRequest(),
      ]);
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const favoriteIds = favoritedPunches.data.map((d) => d.id);
    setActiveFavoriteIds(favoriteIds);
  }, [favoritedPunches]);

  useEffect(() => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      refreshData({ clearDataBeforeFetch: true });
    }, 200);
    // eslint-disable-next-line
  }, [filterData]);

  useEffect(() => {
    const resetFilters = () => {
      localStorage.removeItem("punchFilterData");
      localStorage.removeItem("punchActiveTabIndex");
      setActiveTabIndex(0);
      setFilterData({
        punchList: "",
        punchOwner: "",
        punchOrigin: "",
        punchLocation: "",
        punchCategory: "",
        area: "",
        system: "",
        searchterm: "",
        user: "",
      });
    };

    const handlePageHide = () => {
      if (isMobile) return resetFilters();
    };

    const handleVisibilityChange = () => {
      if (isMobile && document.visibilityState === "hidden") {
        resetFilters();
      }
    };

    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (metaData.data.punchNotifications !== assignedNotifications)
      setAssignedNotifications(metaData.data.punchNotifications ? metaData.data.punchNotifications.notifications : 0);
  }, [metaData.data.punchNotifications]);

  useEffect(() => {
    const unassigned = { label: "Unassigned", value: "unassigned" };

    if (filterData.punchList !== "" && metaData.data.assignees) {
      const filteredAssignees = metaData.data.assignees
        .filter((assignee) => assignee.punchListId === filterData.punchList)
        .map((assignee) => ({ label: assignee.name, value: assignee.id }));

      // Check if current user is in filteredAssignees
      const isUserInFilteredAssignees = filteredAssignees.some((assignee) => assignee.value === user.id);

      if (!isUserInFilteredAssignees) setAssignedNotifications(0);
      else setAssignedNotifications(metaData.data.punchNotifications ? metaData.data.punchNotifications.notifications : 0);

      setAssignees(uniqBy([unassigned, ...filteredAssignees], "label"));
    } else {
      if (
        metaData.data.assignees &&
        (typeof assignees === "undefined" || assignees.length !== metaData.data.assignees.length)
      )
        setAssignees(
          uniqBy([...metaData.data.assignees.map((assignee) => ({ label: assignee.name, value: assignee.id }))], "label")
        );
    }
  }, [filterData.punchList, metaData.data.assignees, user.id]);

  async function refreshData({ clearDataBeforeFetch = false, tabToUpdate = null, status = null } = {}) {
    getStatusCounts({ filterData, setAsLoading: true });

    if (tabToUpdate !== null && status !== null) {
      switch (tabToUpdate) {
        case 0:
          await Promise.all([
            getData({ tabIndex: 0, refreshData: true, filterData, clearDataBeforeFetch }),
            getData({ tabIndex: 3, refreshData: true, filterData, clearDataBeforeFetch }),
            getData({ tabIndex: 4, refreshData: true, filterData, clearDataBeforeFetch }),
          ]);
          break;
        case 1:
          await Promise.all([
            getData({ tabIndex: 1, refreshData: true, filterData, clearDataBeforeFetch }),
            getData({ tabIndex: 3, refreshData: true, filterData, clearDataBeforeFetch }),
            getData({ tabIndex: 4, refreshData: true, filterData, clearDataBeforeFetch }),
          ]);
          break;
        case 2:
          await Promise.all([
            getData({ tabIndex: 2, refreshData: true, filterData, clearDataBeforeFetch }),
            getData({ tabIndex: 3, refreshData: true, filterData, clearDataBeforeFetch }),
            getData({ tabIndex: 4, refreshData: true, filterData, clearDataBeforeFetch }),
          ]);
          break;
        case 3:
          switch (status) {
            case "Open":
              await Promise.all([
                getData({ tabIndex: 0, refreshData: true, filterData, clearDataBeforeFetch }),
                getData({ tabIndex: 3, refreshData: true, filterData, clearDataBeforeFetch }),
                getData({ tabIndex: 4, refreshData: true, filterData, clearDataBeforeFetch }),
              ]);
              break;
            case "Cleared":
              await Promise.all([
                getData({ tabIndex: 1, refreshData: true, filterData, clearDataBeforeFetch }),
                getData({ tabIndex: 3, refreshData: true, filterData, clearDataBeforeFetch }),
                getData({ tabIndex: 4, refreshData: true, filterData, clearDataBeforeFetch }),
              ]);
              break;
            case "Closed":
              await Promise.all([
                getData({ tabIndex: 2, refreshData: true, filterData, clearDataBeforeFetch }),
                getData({ tabIndex: 3, refreshData: true, filterData, clearDataBeforeFetch }),
                getData({ tabIndex: 4, refreshData: true, filterData, clearDataBeforeFetch }),
              ]);
              break;
          }
          break;
        case 4:
          await Promise.all([
            getData({ tabIndex: 3, refreshData: true, filterData, clearDataBeforeFetch }),
            getData({ tabIndex: 4, refreshData: true, filterData, clearDataBeforeFetch }),
          ]);
          break;
      }
    } else {
      metaDataRefetch();

      await Promise.all([
        getData({ tabIndex: 0, refreshData: true, filterData, clearDataBeforeFetch }),
        getData({ tabIndex: 1, refreshData: true, filterData, clearDataBeforeFetch }),
        getData({ tabIndex: 2, refreshData: true, filterData, clearDataBeforeFetch }),
        getData({ tabIndex: 3, refreshData: true, filterData, clearDataBeforeFetch }),
        getData({ tabIndex: 4, refreshData: true, filterData, clearDataBeforeFetch }),
      ]);
    }
  }

  function handleDropdownChange(e, filterData) {
    const newFilterData = { ...filterData, [e.target.name]: e.target.value ? e.target.value : "" };
    localStorage.setItem("punchFilterData", JSON.stringify(newFilterData));
    setFilterData(newFilterData);
  }

  function handleClearSearchTerm(e, filterData) {
    const newFilterData = {
      ...filterData,
      searchterm: "",
    };

    localStorage.setItem("punchFilterData", JSON.stringify(newFilterData));
    setFilterData(newFilterData);
  }

  const closeModal = () => {
    showDialog({
      title: "Hey there!",
      content: "Your changes will not be saved. Are you sure you want to close the form?",
      primaryActionTitle: "Yes, close the form",
      primaryAction: hideModalPage,
      secondaryActionTitle: "No, don't close the form",
    });
  };

  const exportPunches = async () => {
    setExporting(true);

    try {
      const { data: response } = await req().post(`semcompletion/v2/punches/export`);

      const { appConfig = {} } = store.getState();
      const { apiUrl = "" } = appConfig;

      window.location.href = `${apiUrl}exports/${response.fileName}`;

      setExporting(false);
    } catch (error) {
      setExporting(false);
    }
  };

  const exportFavoritePunches = async () => {
    setExportModal(true);
  };

  const checkExportStatus = async (id) => {
    try {
      const { data: response } = await req().get(`semcompletion/v2/punches/favorite/export-request/${id}`);

      if (response.status === "done") {
        notification.success({
          duration: 7,
          message: "SUCCESS",
          description: "The export files have been generated and sent to your email.",
        });
        setIsGenerating(false);
        localStorage.removeItem("favoriteExportRequestId");
      } else if (response.status === "failed") {
        notification.error({
          duration: 7,
          message: "ERROR",
          description: "The generation of favorited punch files export has failed. Please try again.",
        });
        setIsGenerating(false);
        localStorage.removeItem("favoriteExportRequestId");
      } else if (response.status === "generating") {
        // Check again after 5 seconds
        setTimeout(() => checkExportStatus(id), 5000);
      }
    } catch (error) {
      console.log("%c Line:328 🌭 error checking status", "color:#ed9ec7", error);
      notification.error({
        duration: 7,
        message: "ERROR",
        description: "The generation of favorited punch files export has failed. Please try again.",
      });
      setIsGenerating(false);
      localStorage.removeItem("favoriteExportRequestId");
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const downloadFavoritedPunches = async () => {
    if (!user?.email) {
      if (!email) {
        setEmailError("Email is required");
        return;
      }
      if (!validateEmail(email)) {
        setEmailError("Please enter a valid email address");
        return;
      }
    }

    try {
      const { data: response } = await req().post(`semcompletion/v2/punches/favorite/export-request`);

      if (response.status === "generating") {
        setIsGenerating(true);
        localStorage.setItem("favoriteExportRequestId", response.id);

        // Start the generation process without waiting
        req()
          .post(`semcompletion/v2/punches/favorite/report`, {
            email: user?.email || email,
          })
          .then(() => {
            // After generation starts, begin checking status
            checkExportStatus(response.id);
          })
          .catch((error) => {
            console.log("%c Line:328 🌭 error starting generation", "color:#ed9ec7", error);
            notification.error({
              duration: 7,
              message: "ERROR",
              description: "The generation of favorited punch files export has failed. Please try again.",
            });
            setIsGenerating(false);
            localStorage.removeItem("favoriteExportRequestId");
          });

        notification.success({
          duration: 7,
          message: "SUCCESS",
          description: "Your export request has been sent. The files will be generated and sent to your email when ready.",
        });
      }

      setExportModal(false);
      setEmail("");
      setEmailError("");
    } catch (error) {
      console.log("%c Line:328 🌭 error", "color:#ed9ec7", Object.keys(error));
      console.log("%c Line:328 🌭 error", "color:#ed9ec7", error.response);

      if (error.response?.data === "Validation error") {
        notification.warning({
          duration: 7,
          message: "WARNING",
          description: "A similar request is already being processed. Please wait for it to complete.",
        });
      }
    }
  };

  const withFilters = useMemo(() => Object.values(filterData).some((value) => value !== ""), [filterData]);

  return (
    <Page className={container()}>
      <TopBar
        title={`Punch (${selectedProject.number} - ${selectedProject.description})`}
        actionLeft={
          <ArrowBackIcon
            onClick={() => {
              localStorage.removeItem("punchFilterData");
              localStorage.removeItem("punchActiveTabIndex");
              pageNavigator(backButtonURL, "backward");
            }}
          />
        }
        actionHome={
          <ActionWrapper onClick={() => pageNavigator("FRONTPAGE")}>
            <HomeIcon />
          </ActionWrapper>
        }
      />
      <AccessCheck pageId={match.params.pageId}>
        {!metaData.loading && !metaData.error && (
          <>
            <div className="header">
              <div className="row">
                {metaData.data.punchLists.length > 1 && (
                  <DropDown
                    allowClear={true}
                    className={filterData.punchList ? "withValue" : ""}
                    name="punchList"
                    onChange={(e) => handleDropdownChange(e, filterData)}
                    options={[...metaData.data.punchLists.map((d) => ({ label: d.name, value: d.id }))]}
                    placeholder="Punch List"
                    style={{ flexBasis: "20%" }}
                    value={filterData.punchList}
                  />
                )}
                <div className="punch-count-container"></div>
                <div style={{ display: "flex", width: "51%" }}>
                  <Button
                    onClick={exportFavoritePunches}
                    style={{ width: "auto", marginRight: 5 }}
                    disabled={isGenerating || favoritedPunches.data.length === 0}
                  >
                    Download Favorites as PDFs
                  </Button>
                  <Button
                    disabled={exportLoading}
                    loading={exportLoading}
                    onClick={exportPunches}
                    style={{ width: "auto", marginRight: 5 }}
                  >
                    Export Report List
                  </Button>
                  <Button
                    onClick={() =>
                      showModalPage({
                        actionHome: (
                          <ActionWrapper
                            onClick={() => {
                              showDialog({
                                styleType: "warning",
                                title: "Hey there!",
                                content: "Your changes will not be saved. Are you sure you want to close the form?",
                                primaryActionTitle: "Yes, close the form",
                                primaryAction: () => {
                                  pageNavigator("FRONTPAGE");
                                  hideModalPage();
                                },
                                secondaryActionTitle: "No, don't close the form",
                              });
                            }}
                          >
                            <HomeIcon />
                          </ActionWrapper>
                        ),
                        closeCallback: closeModal,
                        content: <PunchModal refreshData={refreshData} />,
                        title: `Create Punch (${selectedProject.number} - ${selectedProject.description})`,
                      })
                    }
                    style={{ width: "auto" }}
                  >
                    Create Punch
                  </Button>
                </div>
              </div>

              <div className="row">
                <SearchInput
                  name="searchterm"
                  onChange={(e) => handleDropdownChange(e, filterData)}
                  onClearSearch={(e) => handleClearSearchTerm(e, filterData)}
                  style={{ width: "100%" }}
                  value={filterData.searchterm}
                />
              </div>
              <Row gutter={[10, 10]}>
                <Col xxl={6} xl={6} lg={6} md={6} sm={8} xs={8}>
                  <DropDown
                    allowClear={true}
                    className={filterData.punchOwner ? "withValue" : ""}
                    name="punchOwner"
                    onChange={(e) => handleDropdownChange(e, filterData)}
                    options={metaData.data.punchOwners.map((d) => ({ label: d.name, value: d.id }))}
                    placeholder="Owner"
                    style={{ fontSize: 13 }}
                    value={filterData.punchOwner}
                  />
                </Col>
                <Col xxl={6} xl={6} lg={6} md={6} sm={8} xs={8}>
                  <DropDown
                    allowClear={true}
                    className={filterData.punchOrigin ? "withValue" : ""}
                    name="punchOrigin"
                    onChange={(e) => handleDropdownChange(e, filterData)}
                    options={metaData.data.punchOrigins.map((d) => ({ label: d.name, value: d.id }))}
                    placeholder="Origin"
                    style={{ fontSize: 13 }}
                    value={filterData.punchOrigin}
                  />
                </Col>
                <Col xxl={6} xl={6} lg={6} md={6} sm={8} xs={8}>
                  <DropDown
                    allowClear={true}
                    className={filterData.punchCategory ? "withValue" : ""}
                    name="punchCategory"
                    onChange={(e) => handleDropdownChange(e, filterData)}
                    options={metaData.data.punchCategories.map((d) => ({ label: d.name, value: d.id }))}
                    placeholder="Category"
                    style={{ fontSize: 13 }}
                    value={filterData.punchCategory}
                  />
                </Col>
                <Col xxl={6} xl={6} lg={6} md={6} sm={8} xs={8}>
                  <DropDown
                    allowClear={true}
                    className={filterData.punchLocation ? "withValue" : ""}
                    name="punchLocation"
                    onChange={(e) => handleDropdownChange(e, filterData)}
                    options={metaData.data.platformLocations.map((d) => ({ label: d.name, value: d.id }))}
                    placeholder="Location"
                    style={{ fontSize: 13 }}
                    value={filterData.punchLocation}
                  />
                </Col>
                <Col xxl={6} xl={6} lg={6} md={6} sm={8} xs={8}>
                  <DropDown
                    allowClear={true}
                    className={filterData.system ? "withValue" : ""}
                    name="system"
                    onChange={(e) => handleDropdownChange(e, filterData)}
                    options={metaData.data.systems.map((d) => ({ label: d.name, value: d.id }))}
                    placeholder="System"
                    style={{ fontSize: 13 }}
                    value={filterData.system}
                  />
                </Col>
                <Col xxl={6} xl={6} lg={6} md={6} sm={8} xs={8}>
                  <DropDown
                    allowClear={true}
                    className={filterData.area ? "withValue" : ""}
                    name="area"
                    onChange={(e) => handleDropdownChange(e, filterData)}
                    options={metaData.data.areas.map((d) => ({ label: d.name, value: d.id }))}
                    placeholder="Area Code"
                    style={{ fontSize: 13 }}
                    value={filterData.area}
                  />
                </Col>
                <Col xxl={6} xl={6} lg={6} md={6} sm={8} xs={8}>
                  <DropDown
                    allowClear={true}
                    className={filterData.user ? "withValue" : ""}
                    name="user"
                    onChange={(e) => handleDropdownChange(e, filterData)}
                    options={metaData.data.users.map((d) => ({ label: d, value: d }))}
                    placeholder="User"
                    style={{ fontSize: 13 }}
                    value={filterData.user}
                  />
                </Col>
                <Col xxl={6} xl={6} lg={6} md={6} sm={8} xs={8}>
                  <DropDown
                    allowClear={true}
                    className={filterData.assignee ? "withValue" : ""}
                    name="assignee"
                    onChange={(e) => handleDropdownChange(e, filterData)}
                    options={assignees}
                    placeholder="Assignee"
                    style={{ fontSize: 13 }}
                    value={filterData.assignee}
                  />
                </Col>
                <Col xxl={6} xl={6} lg={6} md={6} sm={8} xs={8}></Col>
                <Col xxl={6} xl={6} lg={6} md={6} sm={8} xs={8}></Col>
                <Col xxl={6} xl={6} lg={6} md={6} sm={8} xs={8}></Col>
                <Col xxl={6} xl={6} lg={6} md={6} sm={8} xs={8}>
                  <Button
                    disabled={withFilters ? false : true}
                    onClick={() => {
                      localStorage.removeItem("punchFilterData");
                      setFilterData({
                        ...filterData,
                        punchList: "",
                        punchOwner: "",
                        punchOrigin: "",
                        punchCategory: "",
                        punchLocation: "",
                        system: "",
                        area: "",
                        user: "",
                        searchterm: "",
                        assignee: "",
                      });
                    }}
                  >
                    Clear Filters
                  </Button>
                </Col>
              </Row>

              <TabBar
                style={{ margin: "0 -1rem -1rem -1rem" }}
                activeTabIndex={activeTabIndex}
                tabs={tabs.map((tab, index) => ({
                  title: tab.title,
                  color: tab.color,
                  onClick: () => {
                    localStorage.setItem("punchActiveTabIndex", index);
                    setActiveTabIndex(index);
                  },
                }))}
              />
            </div>
            <TabView
              onScrollEnd={(tabIndex) => getData({ tabIndex, filterData })}
              activeTabIndex={activeTabIndex}
              tabs={[
                <Tab
                  activeFavoriteIds={activeFavoriteIds}
                  activeTabIndex={activeTabIndex}
                  filterData={filterData}
                  match={match}
                  punches={openPunches}
                  refreshData={refreshData}
                />,
                <Tab
                  activeFavoriteIds={activeFavoriteIds}
                  activeTabIndex={activeTabIndex}
                  filterData={filterData}
                  match={match}
                  punches={clearedPunches}
                  refreshData={refreshData}
                />,
                <Tab
                  activeFavoriteIds={activeFavoriteIds}
                  activeTabIndex={activeTabIndex}
                  filterData={filterData}
                  match={match}
                  punches={closedPunches}
                  refreshData={refreshData}
                />,
                <Tab
                  activeFavoriteIds={activeFavoriteIds}
                  activeTabIndex={activeTabIndex}
                  filterData={filterData}
                  match={match}
                  punches={favoritedPunches}
                  refreshData={refreshData}
                />,
                <Tab
                  activeFavoriteIds={activeFavoriteIds}
                  activeTabIndex={activeTabIndex}
                  filterData={filterData}
                  match={match}
                  punches={assignedToYouPunches}
                  refreshData={refreshData}
                />,
              ]}
            />
          </>
        )}
      </AccessCheck>
      <Modal open={exportModal} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box sx={downloadModalStyle}>
          <Typography
            id="modal-modal-title"
            style={{ fontFamily: common.fontStack, fontWeight: "bold", fontSize: 18 }}
            variant="h6"
            component="h6"
          >
            Download Favorites PDF File
          </Typography>

          <Typography id="modal-modal-description" style={{ fontFamily: common.fontStack, fontSize: 14 }} sx={{ mb: 3 }}>
            The files will be generated in the background. We'll email you when they're ready.
          </Typography>

          {!user?.email && (
            <TextField
              fullWidth
              label="Email Address"
              variant="outlined"
              value={email}
              onChange={handleEmailChange}
              error={!!emailError}
              helperText={emailError}
              sx={{ mb: 3 }}
            />
          )}

          <div style={{ display: "block", marginTop: 10 }}>
            <MuiButton
              style={{ backgroundColor: "#F4E1DA", color: "#C86746", fontWeight: "bold", textTransform: "capitalize" }}
              onClick={() => {
                setExportModal(false);
                setEmail("");
                setEmailError("");
              }}
              variant="contained"
              sx={{ mr: 3 }}
            >
              Close
            </MuiButton>
            <MuiButton
              onClick={downloadFavoritedPunches}
              variant="contained"
              style={{
                backgroundColor: !user?.email && (!!emailError || !email) ? "#F5F5F5" : "#E7EBED",
                color: !user?.email && (!!emailError || !email) ? "#9E9E9E" : "#103447",
                fontWeight: "bold",
                textTransform: "capitalize",
                opacity: !user?.email && (!!emailError || !email) ? 0.7 : 1,
              }}
              disabled={!user?.email && (!!emailError || !email)}
            >
              Download
            </MuiButton>
          </div>
        </Box>
      </Modal>
    </Page>
  );
}

const Tab = ({ activeFavoriteIds, activeTabIndex, filterData, match, punches, refreshData }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPunchToFavorite, setSelectedPunchToFavorite] = useState(null);

  const { path } = match;
  const { searchterm } = filterData;

  const addPunchToFavorites = async (e, punch) => {
    try {
      setSelectedPunchToFavorite(punch.id);
      setIsLoading(true);

      e.stopPropagation();

      await req().post(`semcompletion/v2/punches/${punch.id}/favorite`);

      await refreshData({ status: punch.status, tabToUpdate: activeTabIndex });

      notification.success({ duration: 7, message: "SUCCESS", description: "Successfully Added to Favorites" });

      setIsLoading(false);
      setSelectedPunchToFavorite(null);
    } catch (error) {
      setIsLoading(false);
      setSelectedPunchToFavorite(null);
      console.log("%c Line:389 🌭 error", "color:#ea7e5c", error);
    }
  };

  const removePunchFromFavorites = async (e, punch) => {
    try {
      setSelectedPunchToFavorite(punch.id);
      setIsLoading(true);

      e.stopPropagation();

      await req().delete(`semcompletion/v2/punches/${punch.id}/favorite`);

      await refreshData({ status: punch.status, tabToUpdate: activeTabIndex });

      notification.success({ duration: 7, message: "SUCCESS", description: "Successfully Removed from Favorites" });

      setIsLoading(false);
      setSelectedPunchToFavorite(null);
    } catch (error) {
      setIsLoading(false);
      setSelectedPunchToFavorite(null);
      console.log("%c Line:414 🥝 error", "color:#6ec1c2", error);
    }
  };

  return (
    <div style={{ padding: "1rem 0" }}>
      {punches.data.length > 0 &&
        !punches.error &&
        punches.data.map((punch, index) => {
          const {
            category: { name: categoryName = "" } = {},
            location: { name: locationName = "" } = {},
            punchOrigin: { name: punchOriginName = "" } = {},
            punchOwner: { name: punchOwnerName = "" } = {},
            system: { name: systemName = "", number: systemNumber = "" } = {},
            area: { code: areaCode, name: areaName = "" } = {},
            workType: { name: workTypeName = "" } = {},
            assignee = "",
            createdDate,
          } = punch;

          const subTitle = [
            punchOwnerName,
            workTypeName,
            locationName,
            punchOriginName,
            categoryName,
            `${systemNumber} - ${systemName}`,
            `${areaCode} - ${areaName}`,
            assignee || "",
            createdDate.split("T").shift(),
          ].filter((d) => d.trim() !== "");

          let borderLeftColor = "";

          switch (punch.status) {
            case "Open":
              borderLeftColor = colors.red;
              break;
            case "Cleared":
              borderLeftColor = colors.vividBlue;
              break;
            case "Closed":
              borderLeftColor = colors.green;
              break;
          }

          return (
            <ListItem
              hasBorderLeft={activeTabIndex === 4}
              borderLeftColor={borderLeftColor}
              onClick={() => void pageNavigator(match.url + "/" + punch.id, "forward")}
              title={highlightMatch(
                `${punch.punchList ? `${punch.punchList.idPrefix}  -` : ""} ${punch.punchId} - ${
                  punch.title ? punch.title : ""
                }`,
                searchterm
              )}
              subTitle={subTitle.join(" | ")}
              iconLeft={
                activeFavoriteIds.includes(punch.id) ? (
                  isLoading && selectedPunchToFavorite && punch.id === selectedPunchToFavorite ? (
                    <InlineSpinner size={18} />
                  ) : (
                    <StarIcon
                      className={starIcon({ status: punch.status })}
                      onClick={(e) => removePunchFromFavorites(e, punch)}
                    />
                  )
                ) : isLoading && selectedPunchToFavorite && punch.id === selectedPunchToFavorite ? (
                  <InlineSpinner size={18} />
                ) : (
                  <StarOutlineIcon onClick={(e) => addPunchToFavorites(e, punch)} />
                )
              }
              iconRight={<ChevronRightIcon />}
              key={punch.id}
              index={index + 1}
              length={punches.data.length}
            />
          );
        })}
      {punches.loading && !punches.error && <InlineSpinner />}
      {!punches.loading && punches.error && <StatusBox />}
      {!punches.loading && !punches.error && punches.data.length === 0 && (
        <StatusBox title="No punches found" icon={<AlertCircleOutlineIcon />} content="  " />
      )}
    </div>
  );
};

const starIcon = ({ status }) => css`
  color: ${status === "Open"
    ? colors.red
    : status === "Cleared"
    ? colors.vividBlue
    : status === "Closed"
    ? colors.green
    : colors.yellow};
`;

const container = () => css`
  .header {
    background-color: ${colors.white};
    padding: 1rem;

    .punch-count-container {
      flex-basis: 50%;
      padding: 0 0.5rem;
      display: flex;

      p {
        margin-right: 0.5rem;
        font-size: 0.9rem;
        font-weight: 700;
      }
    }

    .row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.85rem;

      &:last-of-type {
        margin-bottom: 0;
      }

      .dropdown {
        flex-grow: 1;
        margin: 0 0.35rem;
        flex-basis: 25%;

        &:first-of-type {
          margin-left: 0;
        }
        &:last-of-type {
          margin-right: 0;
        }
      }
    }
  }

  button svg {
    display: inline-block;
    margin-bottom: -0.35rem;
  }
`;

const mapStateToProps = (state) => ({
  selectedProject: state.semcompletion.selectedProject,
  selectedProjectArea: state.semcompletion.selectedProjectArea,
  lang: state.language.language,
  user: state.auth.user,
});

const mapDispatchToProps = (dispatch) => ({
  showModalPage: bindActionCreators(showModalPage, dispatch),
  hideModalPage: bindActionCreators(hideModalPage, dispatch),
  showDialog: bindActionCreators(showDialog, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(PunchOverview);
